import styled from "@emotion/styled";
import {
  type OpportunitiesSearchOptions,
  initialOpportunityOptions,
} from "@repo/api";
import {
  Icon,
  RadioButtonGroup,
  Text,
  Variables,
  chevronDownSmallIcon,
  chevronUpSmallIcon,
} from "@repo/skapa";
import { T, t } from "@repo/translation";
import { FilterPill } from "@repo/ui";

import { SORT_OPTIONS } from "./filter/sortOptions";

const SortSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Variables.space75};
`;

const SortContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Variables.space150};
`;

interface SortButtonProps {
  options: OpportunitiesSearchOptions;
  onSort: (sortKey: string, sortOrder: "asc" | "desc") => void;
  setWidth?: (width: number) => void;
}

export const SortButton = ({ options, onSort, setWidth }: SortButtonProps) => {
  const isDefaultSort =
    options.sortKey === initialOpportunityOptions.sortKey &&
    options.sortOrder === initialOpportunityOptions.sortOrder;

  const sortFieldOptions = SORT_OPTIONS.map(({ sortKey, translationKey }) => ({
    key: sortKey,
    id: `sort-field-${sortKey}`,
    label: t(translationKey),
    name: "sort-field",
    value: sortKey,
    checked: options.sortKey === sortKey,
    onChange: () => onSort(sortKey, options.sortOrder),
  }));

  const sortOrderOptions = [
    {
      key: "asc",
      id: "sort-order-asc",
      label: (
        <>
          {t("label.ascending")} <Icon paths={chevronUpSmallIcon} />
        </>
      ),
      name: "sort-order",
      value: "asc",
      checked: options.sortOrder === "asc",
      onChange: () => onSort(options.sortKey, "asc"),
    },
    {
      key: "desc",
      id: "sort-order-desc",
      label: (
        <>
          {t("label.descending")} <Icon paths={chevronDownSmallIcon} />
        </>
      ),
      name: "sort-order",
      value: "desc",
      checked: options.sortOrder === "desc",
      onChange: () => onSort(options.sortKey, "desc"),
    },
  ];

  return (
    <FilterPill
      id="projects-sort-pill"
      label={t("label.sort")}
      selected={!isDefaultSort}
      setWidth={setWidth}
      dropDownItems={
        <SortContainer>
          <SortSection>
            <Text textStyle="Heading.XS">
              <T translationKey="label.sort_by" />
            </Text>
            <RadioButtonGroup subtle list={sortFieldOptions} />
          </SortSection>
          <SortSection>
            <Text textStyle="Heading.XS">
              <T translationKey="label.order" />
            </Text>
            <RadioButtonGroup subtle list={sortOrderOptions} />
          </SortSection>
        </SortContainer>
      }
    />
  );
};
